import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getTrackRecord } from "../lib/api";
import { Card, Loader, MetricCard } from "./Primitives";
import { POS, NEG } from "../lib/format";

const SEG_NAME = (k) => k === "crypto" ? "Crypto" : k.replace("india-", "India ").replace("global-", "Foreign ").replace("cap", "-cap");
const OUT_COLOR = { win: POS, loss: NEG, open: "#94a3b8", expired: "#ffb300" };
const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`);

export default function TrackRecord() {
  const { data, isLoading } = useQuery({ queryKey: ["track-record"], queryFn: getTrackRecord, staleTime: 300000 });
  if (isLoading || !data) return <Loader label="Scoring past picks against live prices…" />;
  const o = data.overall;
  return (
    <div className="space-y-5" data-testid="track-record">
      <div className="text-xs text-[#64748b]">
        Every Buy / Strong Buy from each daily scan is logged{data.since ? ` since ${data.since}` : ""}. Target touched first = win, stop first = loss, otherwise open (marked to market).
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Picks tracked" value={o.picks} testid="tr-picks" />
        <MetricCard label="Closed" value={o.closed} testid="tr-closed" />
        <MetricCard label="Win rate" value={o.win_rate == null ? "—" : `${o.win_rate}%`} accent={POS} testid="tr-winrate" />
        <MetricCard label="Avg return" value={pct(o.avg_return_pct)} accent={o.avg_return_pct >= 0 ? POS : NEG} testid="tr-avg" />
      </div>
      <Card className="p-4 overflow-x-auto" testid="tr-segments">
        <table className="w-full text-xs font-mono min-w-[520px]">
          <thead><tr className="text-[#64748b] text-left">{["Segment", "Picks", "Wins", "Losses", "Open", "Win rate", "Avg return"].map((h) => <th key={h} className="py-2 pr-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {data.segments.map((s) => (
              <tr key={s.key} className="border-t border-[#1e293b] text-[#cbd5e1]" data-testid={`tr-seg-${s.key}`}>
                <td className="py-2 pr-3">{SEG_NAME(s.key)}</td><td>{s.picks}</td><td style={{ color: POS }}>{s.wins}</td>
                <td style={{ color: NEG }}>{s.losses}</td><td>{s.open}</td><td>{s.win_rate == null ? "—" : `${s.win_rate}%`}</td>
                <td style={{ color: s.avg_return_pct >= 0 ? POS : NEG }}>{pct(s.avg_return_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.segments.length && <div className="text-center text-[#64748b] py-6" data-testid="tr-empty">No picks logged yet — the first scan populates this.</div>}
      </Card>
      <div className="space-y-2" data-testid="tr-recent">
        {data.recent.slice(0, 30).map((r) => (
          <div key={`${r.date}-${r.symbol}`} className="flex items-center gap-3 text-xs font-mono border-b border-[#1e293b] py-2">
            <span className="text-[#64748b] w-20 shrink-0">{r.date}</span>
            <span className="text-[#f8fafc] truncate flex-1">{r.name}</span>
            <span className="uppercase w-16 text-right" style={{ color: OUT_COLOR[r.outcome] }}>{r.outcome}</span>
            <span className="w-16 text-right" style={{ color: r.return_pct >= 0 ? POS : NEG }}>{pct(r.return_pct)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
